// Reads the SSO callback parameters off /login and removes them from the
// address bar before anything else can observe them (DEV-1032).
//
// kvasir redirects the login dialog back to /login?code=<code>&state=<state>
// (the PKCE flow), or /login?token=<jwt> on a kvasir that predates it. Either
// value is a credential: left in the URL it would be recorded by Sentry
// (pageload transaction, breadcrumbs, Replay) and by anything else that reads
// location.href. main.tsx imports ./stripCallbackParams before App, so this
// runs before Sentry.init; Login also calls it, which covers tests and is a
// no-op once the parameters have been captured.

export const CALLBACK_PARAMS = ['code', 'state', 'token'] as const;

export interface CallbackParams {
  code: string;
  state: string;
  token: string;
}

let captured: CallbackParams | null = null;

export function captureCallbackParams(): CallbackParams {
  if (captured) return captured;

  const result: CallbackParams = { code: '', state: '', token: '' };
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    captured = result;
    return result;
  }

  if (url.pathname.replace(/\/+$/, '') !== '/login') {
    captured = result;
    return result;
  }

  let found = false;
  for (const name of CALLBACK_PARAMS) {
    if (url.searchParams.has(name)) {
      result[name] = url.searchParams.get(name) || '';
      url.searchParams.delete(name);
      found = true;
    }
  }

  if (found) {
    try {
      window.history.replaceState(
        window.history.state,
        '',
        url.pathname + url.search + url.hash,
      );
    } catch {
      // Nothing to do: the values are captured either way.
    }
  }

  captured = result;
  return result;
}

/** Test helper: forget the captured parameters. */
export function resetCallbackParamsForTests(): void {
  captured = null;
}
